/**
 * Die Hülle: Schiene, Kopfleiste, Inhalt.
 *
 * Der Entwurf baut die Anwendung als **ganzseitiges Gitter mit innerem
 * Rollen**: `height: 100vh; overflow: hidden`, links die Schiene in fester
 * Breite, rechts eine Spalte aus Kopfleiste und einem eigenen Rollbereich.
 * Das ist die eigentliche strukturelle Änderung gegenüber vorher — bisher
 * rollte die Seite als Ganzes und die Leiste lag `fixed` daneben.
 *
 * Der Unterschied ist nicht kosmetisch: Kopfleiste und Schiene bleiben stehen,
 * ohne `sticky`, ohne Weichzeichner und ohne dass ein `z-index` entscheiden
 * muss, was über was liegt.
 *
 * Unter 1024px gibt es keine Schiene — dort trägt eine Kopfzeile die Wortmarke
 * und öffnet dieselbe Schiene in einer Schublade. Damit hat die Navigation
 * **eine** Quelle statt zweier Listen, die auseinanderlaufen; die bisherige
 * `MobileSidebar` führte nur drei der Punkte und kannte die Projektgruppe nicht.
 */

import { Fragment, useEffect, useState } from "react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { Schiene } from "@/components/entwurf/Schiene";
import { Kopfleiste } from "@/components/entwurf/Kopfleiste";
import { Suchfenster } from "@/components/entwurf/Suchfenster";
import { Wortmarke } from "@/components/Wortmarke";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "@/components/Drawer";
import { cx, focusRing } from "@/lib/utils";
import { TASTATUR_BEFEHLE, treffeBefehl } from "@/lib/tastatur";
import { brotkrume } from "@/lib/brotkrume";
import { istRandlos } from "@/lib/rahmen";
import { ProjektAuswahlProvider, ALLE_PROJEKTE, useProjektAuswahl } from "@/lib/projekt-auswahl";
import { useProjekte } from "@/lib/haken";

export function Huelle({
  onNeueAufgabe,
  onHilfe,
}: {
  onNeueAufgabe?: () => void;
  onHilfe?: () => void;
}) {
  // Der Auswahlspeicher muss über dem Gerüst liegen — Schiene und Kopfleiste
  // lesen ihn beide.
  return (
    <ProjektAuswahlProvider>
      <Geruest onNeueAufgabe={onNeueAufgabe} onHilfe={onHilfe} />
    </ProjektAuswahlProvider>
  );
}

function Geruest({
  onNeueAufgabe,
  onHilfe,
}: {
  onNeueAufgabe?: () => void;
  onHilfe?: () => void;
}) {
  const [hilfeOffen, setzeHilfe] = useState(false);
  const [schubladeOffen, setzeSchublade] = useState(false);
  const [sucheOffen, setzeSuche] = useState(false);
  const pfad = useRouterState({ select: (s) => s.location.pathname });
  const suche = useRouterState({ select: (s) => s.location.search }) as Record<
    string,
    unknown
  >;
  const { auswahl } = useProjektAuswahl();
  const projekte = useProjekte();
  const seitenModus = /\/seiten(\/|$)/.test(pfad);
  const randlos = istRandlos(pfad);

  const gewaehlt =
    auswahl === ALLE_PROJEKTE
      ? undefined
      : (projekte.data ?? []).find((p) => p.id === auswahl);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const b = treffeBefehl(e);
      if (!b) return;
      e.preventDefault();
      if (b.id === "aufgabe_neu") onNeueAufgabe?.();
      if (b.id === "suche") setzeSuche((v) => !v);
      if (b.id === "hilfe") {
        setzeHilfe((v) => !v);
        onHilfe?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onNeueAufgabe, onHilfe]);

  // Die Schublade schließt beim Wechsel — sonst bleibt sie über der Seite
  // stehen, auf die sie gerade geführt hat.
  useEffect(() => {
    setzeSchublade(false);
  }, [pfad]);

  return (
    <div className="flex h-screen overflow-hidden bg-papier text-tinte">
      <Schiene className="hidden lg:flex" />

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Unter 1024px: Wortmarke und Schublade statt Schiene. */}
        <div className="flex h-[60px] flex-none items-center justify-between border-b border-linie px-4 lg:hidden">
          <Link to="/" aria-label="Zur Übersicht" className={cx(focusRing)}>
            <Wortmarke groesse={26} mitZeichen={false} />
          </Link>
          <Drawer open={schubladeOffen} onOpenChange={setzeSchublade}>
            <DrawerTrigger asChild>
              <button
                type="button"
                aria-label="Navigation öffnen"
                className={cx("p-1.5 text-tinte3", focusRing)}
              >
                <Menu className="size-6 shrink-0" aria-hidden="true" />
              </button>
            </DrawerTrigger>
            {/*
              Die Schublade trägt die Schiene unverändert. `p-0` und
              `max-w-[236px]`, weil die Schiene ihre Breite und ihre Polsterung
              selbst mitbringt.

              **Der Rest dieser Klassenliste macht aus Tremors Karte eine
              Schublade.** `DrawerContent` bringt von Haus aus
              `fixed inset-y-2 mx-auto w-[95vw] rounded-xl border shadow-xl`
              und `max-sm:inset-x-2` mit — auf dem Handy also eine
              freischwebende, mittig zentrierte Karte mit acht Pixeln Luft
              ringsum. Mit `max-w-[236px]` wurde daraus ein schmaler Kasten
              irgendwo in der Mitte des Bildschirms; die Navigation hing an
              nichts.

              Die kopierte Tremor-Datei bleibt unangetastet — das ist die
              Bedingung dafür, dass die Tokenschicht überhaupt greift. Was hier
              anders sein soll, wird hier gesagt: linke Kante, volle Höhe,
              keine Rundung, kein Rand. `cx` läuft über `twMerge`, deshalb
              gewinnen diese Angaben gegen die der Vorlage.
            */}
            <DrawerContent
              className={cx(
                "border-0 p-0 shadow-none",
                "inset-y-0 left-0 right-auto mx-0 rounded-none",
                "max-sm:inset-x-auto max-sm:left-0 max-sm:inset-y-0",
                "w-[236px] max-w-[236px]",
              )}
            >
              <Schiene className="h-full w-full" />
            </DrawerContent>
          </Drawer>
        </div>

        <Kopfleiste
          className="hidden lg:flex"
          kuerzel={gewaehlt?.kuerzel ?? undefined}
          teile={brotkrume(pfad, suche["reiter"])}
          onSuche={() => setzeSuche(true)}
        />

        {/*
          Der eigene Rollbereich. `min-h-0` ist die Bedingung dafür, dass er
          rollt statt zu wachsen — ohne die Angabe rechnet Flex die Höhe aus
          dem Inhalt und die ganze Seite wird länger als der Bildschirm.

          Die Abstände sind die des Entwurfs: 56px oben, 36px seitlich, 96px
          unten. `space-y-6` bleibt vorläufig für die noch nicht umgestellten
          Seiten stehen — der Entwurf setzt seine Abstände je Abschnitt selbst,
          und ohne diese Zwischenlösung stünden die alten Seiten bis zu ihrem
          Umbau ohne jeden senkrechten Rhythmus da.
        */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {/*
            Randlose Seiten bekommen keinen Rahmen — sie tragen die schwarze
            Tafel bis an die Kante oder setzen ihre Spalten über die volle
            Breite und bringen ihre Abstände selbst mit. `min-h-full` bleibt,
            damit `align-items: stretch` der Tafel etwas zum Ausfüllen hat;
            ohne die Angabe endet sie an ihrem Inhalt statt am Fensterrand.

            **`flex flex-col` steht dazu, damit die Seite die Höhe erben kann.**
            `min-height: 100%` wirkt nur an dieser Stelle: Der Rollbereich
            darüber hat eine feste Höhe, dieser Kasten dagegen nicht — ein
            `min-h-full` weiter innen löst gegen `height: auto` zu Null auf und
            hilft niemandem. Als Spalte kann eine Seite `flex-1` sagen und wird
            genau so hoch wie der Rollbereich. Wer stattdessen im Kind das
            Fenster ausmisst (`100dvh` minus geschätzter Kopfleiste), rechnet an
            der falschen Größe und rollt um die Differenz über das Seitenende
            hinaus — genau das war bei den Wissensseiten der Fall.
          */}
          <div
            className={cx(
              randlos
                ? "flex min-h-full flex-col"
                : "px-4 py-8 sm:px-6 lg:px-9 lg:pb-24 lg:pt-14",
              !randlos && (seitenModus ? "flex min-h-0 flex-col" : "space-y-6"),
            )}
          >
            <Outlet />
          </div>
        </div>
      </div>

      <Suchfenster offen={sucheOffen} setzeOffen={setzeSuche} />

      {hilfeOffen ? (
        <div
          className="animate-slideUpAndFade fixed bottom-4 right-4 z-50 max-w-xs border border-linie bg-wisch p-4 text-sm"
          role="dialog"
          aria-label="Tastenkürzel"
        >
          <p className="font-medium tracking-titel text-tinte">Tastenkürzel</p>
          {/*
            Die Tasten stehen in einer eigenen Spalte, damit die Beschreibungen
            auf einer Kante beginnen. Vorher liefen sie mit der Tastenbreite
            mit — bei „Umschalt+C" neben „?" sah das nach zwei Listen aus.
          */}
          {/*
            Aus der Liste erzeugt, nicht abgeschrieben. Vorher standen hier zwei
            Kürzel fest im Markup — mit ⌘K wären es drei gewesen, und die
            Übersicht hätte geschwiegen. Wer einen Befehl hinzufügt, muss ihn
            nicht an zwei Stellen pflegen.
          */}
          <dl className="mt-3 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 text-tinte3">
            {TASTATUR_BEFEHLE.map((b) => (
              <Fragment key={b.id}>
                <dt>
                  <kbd className="border border-linie bg-code px-1.5 py-0.5 font-mono text-[11px] font-medium text-tinte">
                    {b.label}
                  </kbd>
                </dt>
                <dd>{b.beschreibung}</dd>
              </Fragment>
            ))}
          </dl>
          <button
            type="button"
            className={cx(
              "mt-3 font-mono text-[12px] text-akzent underline decoration-1 underline-offset-[3px] hover:text-akzenttief",
              focusRing,
            )}
            onClick={() => setzeHilfe(false)}
          >
            Schließen
          </button>
        </div>
      ) : null}
    </div>
  );
}
